import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Textarea from "../../components/common/Textarea";
import {
  useGetOpenTillSessionsQuery,
  useGetReconciliationQuery,
  useGetTillsQuery,
  useRecalculateReconciliationMutation,
  useSaveReconciliationLinesMutation,
  useGetTillSessionQuery,
} from "../../store/api";
import { usePosRealtime } from "../../hooks/usePosRealtime";
import {
  formatCurrency,
  getCashingUpSessionStatusLabel,
  getCashingUpStatusTone,
  normalizeMoney,
} from "../../utils/cashingUp";
import { useDeviceCashSessionResolution } from "../../hooks/useDeviceCashSession";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const reconciliationLineFromTender = (tender, index) => ({
  reconciliationLineId: tender?.reconciliationLineId || null,
  tenderType: tender?.tenderType || "CASH",
  providerOrChannel: tender?.providerOrChannel || "",
  actualAmount:
    tender?.actualAmount !== undefined && tender?.actualAmount !== null
      ? String(tender.actualAmount)
      : "",
  settlementRef: tender?.settlementRef || "",
  slipAttachmentRef: tender?.slipAttachmentRef || "",
  notes: tender?.notes || "",
  key:
    tender?.reconciliationLineId ||
    `${tender?.tenderType || "line"}-${index}-${tender?.providerOrChannel || "default"}`,
});

const blankReconciliationLine = (index) => ({
  reconciliationLineId: null,
  tenderType: "CASH",
  providerOrChannel: "",
  actualAmount: "",
  settlementRef: "",
  slipAttachmentRef: "",
  notes: "",
  key: `blank-${Date.now()}-${index}`,
});

const COMMON_TENDER_TYPES = [
  { tenderType: "CASH", providerOrChannel: "POS" },
  { tenderType: "CARD", providerOrChannel: "Terminal" },
  { tenderType: "UPI", providerOrChannel: "Gateway" },
  { tenderType: "GIFT_CARD", providerOrChannel: "POS Report" },
];

const Reconciliation = ({
  embedded = false,
  selectedSessionId: selectedSessionIdProp,
} = {}) => {
  const { selectedStoreId } = useStore();
  const isSelectedSessionControlled =
    selectedSessionIdProp !== undefined && selectedSessionIdProp !== null;
  const [internalSelectedSessionId, setInternalSelectedSessionId] = useState("");
  const selectedSessionId = isSelectedSessionControlled
    ? selectedSessionIdProp
    : internalSelectedSessionId;
  const [reconciliationLines, setReconciliationLines] = useState([]);
  const [reconciliationLinesMessage, setReconciliationLinesMessage] =
    useState("");
  const [localError, setLocalError] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [editingLine, setEditingLine] = useState(blankReconciliationLine(0));

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    isError: sessionsIsError,
    error: sessionsError,
    refetch: refetchSessions,
  } = useGetOpenTillSessionsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const sessions = useMemo(
    () => (Array.isArray(sessionsData) ? sessionsData : []),
    [sessionsData],
  );

  const deviceSessionResolution = useDeviceCashSessionResolution(
    sessions,
    Boolean(selectedStoreId),
  );

  useEffect(() => {
    if (isSelectedSessionControlled) {
      return;
    }
    if (!selectedStoreId) {
      setInternalSelectedSessionId("");
      return;
    }
    if (sessionsLoading || deviceSessionResolution.isLoading) {
      return;
    }

    if (deviceSessionResolution.status === "ready") {
      setInternalSelectedSessionId(deviceSessionResolution.selectedSessionId);
      return;
    }

    setInternalSelectedSessionId("");
  }, [
    deviceSessionResolution.isLoading,
    deviceSessionResolution.selectedSessionId,
    deviceSessionResolution.status,
    isSelectedSessionControlled,
    sessionsLoading,
    selectedStoreId,
  ]);

  const {
    data: sessionData,
    isLoading: sessionLoading,
    isError: sessionIsError,
    error: sessionError,
  } = useGetTillSessionQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });

  const session = sessionData && typeof sessionData === "object" ? sessionData : null;

  const { data: tillsData } = useGetTillsQuery(
    { storeId: session?.storeId || selectedStoreId, activeOnly: false },
    { skip: !selectedStoreId && !session?.storeId },
  );

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );
  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );

  const {
    data: reconciliationData,
    isError: reconciliationIsError,
    error: reconciliationError,
    refetch: refetchReconciliation,
  } = useGetReconciliationQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });

  const [recalculateReconciliation, recalculateState] =
    useRecalculateReconciliationMutation();
  const [saveReconciliationLines, saveReconciliationState] =
    useSaveReconciliationLinesMutation();

  const handleCashSessionChanged = useCallback(
    (payload) => {
      const changedSessionId = payload?.cashSessionId || payload?.sessionId;
      if (!changedSessionId || changedSessionId === selectedSessionId) {
        refetchReconciliation();
      }
      refetchSessions();
    },
    [refetchReconciliation, refetchSessions, selectedSessionId],
  );

  usePosRealtime({
    onCashSessionChanged: handleCashSessionChanged,
  });

  const tenderSummaries = useMemo(
    () =>
      Array.isArray(reconciliationData?.tenderSummaries)
        ? reconciliationData.tenderSummaries
        : [],
    [reconciliationData?.tenderSummaries],
  );

  useEffect(() => {
    if (!reconciliationData) return;
    setReconciliationLines(
      tenderSummaries.map((tender, index) => reconciliationLineFromTender(tender, index)),
    );
  }, [reconciliationData, tenderSummaries]);

  const addReconciliationLine = () => {
    setReconciliationLines((prev) => [
      ...prev,
      blankReconciliationLine(prev.length),
    ]);
  };

  const saveLineModal = () => {
    if (editingLineIndex === null) return;
    setReconciliationLines((prev) =>
      prev.map((line, idx) =>
        idx === editingLineIndex ? { ...editingLine, key: line.key } : line,
      ),
    );
    setEditModalOpen(false);
    setEditingLineIndex(null);
    setEditingLine(blankReconciliationLine(0));
  };

  const handleRecalculate = async () => {
    if (!selectedSessionId) {
      setLocalError("Select a session first.");
      return;
    }

    try {
      await recalculateReconciliation({ cashSessionId: selectedSessionId }).unwrap();
      refetchReconciliation();
      setReconciliationLinesMessage("Reconciliation recalculated.");
    } catch (err) {
      setReconciliationLinesMessage(
        getErrorMessage(err, "Failed to recalculate reconciliation."),
      );
    }
  };

  const saveReconciliation = async () => {
    if (!selectedSessionId) {
      setLocalError("Select a session first.");
      return;
    }

    const payload = reconciliationLines.map((line) => ({
      reconciliationLineId: line.reconciliationLineId || null,
      tenderType: line.tenderType,
      providerOrChannel: line.providerOrChannel.trim() || null,
      actualAmount:
        line.actualAmount === "" || Number.isNaN(Number(line.actualAmount))
          ? null
          : Number(line.actualAmount),
      settlementRef: line.settlementRef.trim() || null,
      slipAttachmentRef: line.slipAttachmentRef.trim() || null,
      notes: line.notes.trim() || null,
    }));

    try {
      await saveReconciliationLines({
        cashSessionId: selectedSessionId,
        payload,
      }).unwrap();
      setReconciliationLinesMessage("Reconciliation lines saved.");
      refetchReconciliation();
    } catch (err) {
      setReconciliationLinesMessage(
        getErrorMessage(err, "Failed to save reconciliation lines."),
      );
    }
  };

  const updateLineField = useCallback((index, field, value) => {
    setReconciliationLines((prev) =>
      prev.map((line, idx) => (idx === index ? { ...line, [field]: value } : line)),
    );
  }, []);

  const addCommonTenderLines = useCallback(() => {
    setReconciliationLines((prev) => {
      const existing = new Set(
        prev.map((line) =>
          `${String(line.tenderType || "").toUpperCase()}|${String(
            line.providerOrChannel || "",
          ).toLowerCase()}`,
        ),
      );

      const additions = COMMON_TENDER_TYPES.filter((item) => {
        const key = `${item.tenderType}|${item.providerOrChannel.toLowerCase()}`;
        return !existing.has(key);
      }).map((item, index) => ({
        ...blankReconciliationLine(prev.length + index),
        tenderType: item.tenderType,
        providerOrChannel: item.providerOrChannel,
      }));

      if (!additions.length) {
        setReconciliationLinesMessage("Common tenders are already added.");
        return prev;
      }

      return [...prev, ...additions];
    });
  }, []);

  const removeReconciliationLine = useCallback((index) => {
    setReconciliationLines((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const rows = useMemo(
    () =>
      reconciliationLines.map((line, index) => {
        const tenderSummary = tenderSummaries[index] || {};
        const expectedAmount = normalizeMoney(tenderSummary.expectedAmount || 0);
        const actualAmount =
          line.actualAmount === "" || Number.isNaN(Number(line.actualAmount))
            ? null
            : normalizeMoney(line.actualAmount);
        const rowDifference =
          actualAmount === null ? null : normalizeMoney(actualAmount - expectedAmount);

        return {
          id: line.key || index,
          tenderType: (
              <div className="space-y-0.5">
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {line.tenderType || "-"}
                </div>
                <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {tenderSummary.providerOrChannel || line.providerOrChannel || "-"}
                </div>
              </div>
          ),
          expectedAmount: formatCurrency(expectedAmount),
          actualAmount: (
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={line.actualAmount}
              onChange={(e) => updateLineField(index, "actualAmount", e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              placeholder="0.00"
            />
          ),
          variance: (
            <span
              className={
                rowDifference === null
                  ? "text-slate-500 dark:text-slate-400"
                  : rowDifference >= 0
                    ? "font-semibold text-emerald-700 dark:text-emerald-300"
                    : "font-semibold text-rose-700 dark:text-rose-300"
              }
            >
              {rowDifference === null ? "-" : formatCurrency(rowDifference)}
            </span>
          ),
          sourceReference: (
            <input
              type="text"
              value={line.providerOrChannel}
              onChange={(e) =>
                updateLineField(index, "providerOrChannel", e.target.value)
              }
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              placeholder="Source / reference"
            />
          ),
          notes: (
            <input
              type="text"
              value={line.notes}
              onChange={(e) => updateLineField(index, "notes", e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              placeholder="Notes"
            />
          ),
          action: (
            <Button
              size="sm"
              variant="danger"
              onClick={() => removeReconciliationLine(index)}
            >
              Remove
            </Button>
          ),
        };
      }),
    [reconciliationLines, removeReconciliationLine, tenderSummaries, updateLineField],
  );

  const columns = [
    { key: "tenderType", label: "Tender Type" },
    { key: "expectedAmount", label: "Expected (CA$)" },
    { key: "actualAmount", label: "Actual (CA$)" },
    { key: "variance", label: "Difference (CA$)" },
    { key: "sourceReference", label: "Source / Reference" },
    { key: "notes", label: "Notes" },
    { key: "action", label: "Action" },
  ];

  const expectedCashValue = normalizeMoney(
    reconciliationData?.expectedCash?.expectedCash || 0,
  );
  const actualCashValue = useMemo(
    () =>
      reconciliationLines.reduce((total, line) => {
        const value =
          line.actualAmount === "" || Number.isNaN(Number(line.actualAmount))
            ? 0
            : Number(line.actualAmount);
        return total + value;
      }, 0),
    [reconciliationLines],
  );
  const cashVarianceValue = normalizeMoney(actualCashValue - expectedCashValue);
  const tillLabel = useMemo(() => {
    const till = tillById.get(session?.tillId);
    return (
      session?.tillName ||
      till?.tillName ||
      till?.tillCode ||
      session?.tillId ||
      "-"
    );
  }, [session, tillById]);
  const openedAtLabel = session?.openedAt
    ? new Date(session.openedAt).toLocaleString()
    : "-";

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Reconciliation"
          subtitle="Review expected vs actual cash and verify each payment type."
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                refetchSessions();
                refetchReconciliation();
              }}
            >
              Refresh
            </Button>
          }
        />
      )}

      {!selectedStoreId && (
        <Card>
          <Alert tone="warning">No store is selected. Choose a store first.</Alert>
        </Card>
      )}

      {selectedStoreId && (
        <Card className="space-y-6 p-5 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                Reconciliation
              </h2>
            </div>
            <Badge tone={getCashingUpStatusTone(session?.status)}>
              {getCashingUpSessionStatusLabel(session?.status)}
            </Badge>
          </div>

          {(sessionsLoading || sessionLoading) && (
            <Alert tone="info">Loading session details...</Alert>
          )}
          {!sessionsLoading &&
            !sessionLoading &&
            !deviceSessionResolution.isLoading &&
            !isSelectedSessionControlled &&
            deviceSessionResolution.status !== "ready" && (
              <Alert tone="warning">{deviceSessionResolution.message}</Alert>
            )}
          {sessionsIsError && (
            <Alert tone="error">
              {getErrorMessage(sessionsError, "Failed to load sessions.")}
            </Alert>
          )}
          {sessionIsError && (
            <Alert tone="error">
              {getErrorMessage(sessionError, "Failed to load session details.")}
            </Alert>
          )}
          {reconciliationIsError && (
            <Alert tone="error">
              {getErrorMessage(
                reconciliationError,
                "Failed to load reconciliation summary.",
              )}
            </Alert>
          )}

          <div className="grid gap-2 xl:grid-cols-5">
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-950/60">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Till
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {tillLabel}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-950/60">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Opened At
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {openedAtLabel}
              </p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Expected Cash (Calculated)
              </p>
              <p className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-300">
                {formatCurrency(expectedCashValue)}
              </p>
              <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                View breakdown from the current reconciliation.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-950/60">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Actual Cash (Counted)
              </p>
              <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                {formatCurrency(actualCashValue)}
              </p>
              <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                Sum of the actual amounts entered below.
              </p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 shadow-sm dark:border-rose-900/60 dark:bg-rose-950/30">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Cash Variance
              </p>
              <p
                className={`mt-1 text-lg font-semibold ${
                  cashVarianceValue >= 0
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-rose-600 dark:text-rose-300"
                }`}
              >
                {formatCurrency(cashVarianceValue)}
              </p>
              <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                Difference between counted cash and expected cash.
              </p>
            </div>
          </div>

          <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-700">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Tender Reconciliation
                </h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Enter actual amounts as per settlement reports or terminal summaries.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <Button size="sm" variant="secondary" onClick={addCommonTenderLines}>
                  Add from Common Tenders
                </Button>
                <Button size="sm" onClick={addReconciliationLine}>
                  Add Line
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="overflow-x-auto">
                <table className="min-w-[1200px] table-fixed divide-y divide-slate-200 dark:divide-slate-700">
                  <thead className="bg-slate-50 dark:bg-slate-900/60">
                    <tr>
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-950/60">
                    {rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                        >
                          No reconciliation lines yet. Use Recalculate or Add Line.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id}>
                          <td className="w-[12%] px-3 py-4 align-middle text-sm text-slate-900 dark:text-slate-100">
                            {row.tenderType}
                          </td>
                          <td className="w-[11%] px-3 py-4 align-middle text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {row.expectedAmount}
                          </td>
                          <td className="w-[12%] px-3 py-4 align-middle">{row.actualAmount}</td>
                          <td className="w-[11%] px-3 py-4 align-middle text-sm">{row.variance}</td>
                          <td className="w-[22%] px-3 py-4 align-middle">{row.sourceReference}</td>
                          <td className="w-[22%] px-3 py-4 align-middle">{row.notes}</td>
                          <td className="w-[10%] px-3 py-4 align-middle">{row.action}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs text-indigo-700 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-200">
              Enter actual amounts from settlement reports, bank summaries, or gateway reports,
              then save to update the reconciliation totals.
            </p>

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-700">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRecalculate}
                disabled={recalculateState.isLoading}
              >
                {recalculateState.isLoading ? "Recalculating..." : "Recalculate"}
              </Button>
              <Button
                size="sm"
                onClick={saveReconciliation}
                disabled={saveReconciliationState.isLoading}
              >
                {saveReconciliationState.isLoading ? "Saving..." : "Save Lines"}
              </Button>
            </div>
          </div>

          {reconciliationLinesMessage && <Alert tone="info">{reconciliationLinesMessage}</Alert>}
          {localError && <Alert tone="error">{localError}</Alert>}
          {saveReconciliationState.isError && (
            <Alert tone="error">
              {getErrorMessage(
                saveReconciliationState.error,
                "Failed to save reconciliation lines.",
              )}
            </Alert>
          )}
        </Card>
      )}

      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Reconciliation Line"
        confirmText="Save Line"
        onConfirm={saveLineModal}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Tender Type"
              value={editingLine.tenderType}
              onChange={(e) =>
                setEditingLine((prev) => ({ ...prev, tenderType: e.target.value }))
              }
            >
              <option value="CASH">CASH</option>
              <option value="CARD">CARD</option>
              <option value="UPI">UPI</option>
              <option value="WALLET">WALLET</option>
              <option value="OTHER">OTHER</option>
            </Select>
            <Input
              label="Provider / Channel"
              value={editingLine.providerOrChannel}
              onChange={(e) =>
                setEditingLine((prev) => ({
                  ...prev,
                  providerOrChannel: e.target.value,
                }))
              }
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Actual Amount"
              type="number"
              value={editingLine.actualAmount}
              onChange={(e) =>
                setEditingLine((prev) => ({ ...prev, actualAmount: e.target.value }))
              }
            />
            <div />
          </div>
          <Textarea
            label="Notes"
            value={editingLine.notes}
            onChange={(e) =>
              setEditingLine((prev) => ({ ...prev, notes: e.target.value }))
            }
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Reconciliation;
